import { useState } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

interface AddOwnerCaretakerModalProps {
  open: boolean;
  onClose: () => void;
  petName: string;
  onSubmit: (email: string, role: 'CO_OWNER' | 'CARETAKER') => Promise<void>;
}

export function AddOwnerCaretakerModal({
  open,
  onClose,
  petName,
  onSubmit,
}: AddOwnerCaretakerModalProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'CO_OWNER' | 'CARETAKER'>('CARETAKER');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await onSubmit(trimmed, role);
      setEmail('');
      setRole('CARETAKER');
      setNote('');
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-owner-title"
    >
      {/* Overlay - click to close */}
      <div
        className="absolute inset-0 bg-black/50"
        aria-hidden="true"
        onClick={onClose}
      />
      {/* Modal panel - explicit stacking and visibility */}
      <div
        className="relative z-10 w-full max-w-md rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#16151D] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2
            id="add-owner-title"
            className="text-lg font-semibold text-gray-900 dark:text-white"
          >
            {t('petOverview.addOwnerOrCaretaker')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-white/70 dark:hover:text-white dark:hover:bg-white/10 transition-colors"
            aria-label={t('common.close', 'Close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="add-owner-email">
              {t('petOverview.emailAddressRequired')}
            </Label>
            <Input
              id="add-owner-email"
              type="email"
              placeholder={t('petOverview.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="add-owner-role">{t('petOverview.roleLabel')}</Label>
            <select
              id="add-owner-role"
              value={role}
              onChange={(e) =>
                setRole(e.target.value as 'CO_OWNER' | 'CARETAKER')
              }
              className="mt-1 flex h-9 w-full items-center justify-between rounded-md border border-input bg-input-background px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30 dark:hover:bg-input/50 dark:border-white/20 dark:text-white"
            >
              <option value="CARETAKER">{t('petOverview.caretaker')}</option>
              <option value="CO_OWNER">{t('petOverview.coParent')}</option>
            </select>
          </div>
          <div>
            <Label htmlFor="add-owner-note">
              {t('petOverview.noteOptional')}
            </Label>
            <Input
              id="add-owner-note"
              type="text"
              placeholder={t('petOverview.notePlaceholderInvite', {
                name: petName,
              })}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="mt-1"
            />
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={submitting || !email.trim()}>
              {submitting
                ? t('petOverview.sendingInvitation')
                : t('petOverview.sendInvitation')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
